import { coercePreferences, type PlannerPreferences } from "./preferences";
import { stylePatchPathsFor } from "./day-styles";
import type { ItinerarySuccess } from "./api-types";

export type GroupJourneyMember = {
  ability: PlannerPreferences["ability"];
  startPoint: string;
  startTime: string;
  finishPoint: string;
  endTime: string;
};
export type GroupJourneyArrangement = {
  version: 1;
  slug: string;
  planDate: string;
  preferences: PlannerPreferences;
  meeting: { nodeId: string; time: string; pauseMinutes: number };
  groups: [GroupJourneyMember, GroupJourneyMember];
};
export type GroupJourneyContext = {
  arrangement: GroupJourneyArrangement;
  subgroup: 0 | 1;
  /** First movement step after the agreed meeting and pause. */
  meetingLegIndex: number;
};
export type GroupJourneyResult = {
  arrangement: GroupJourneyArrangement;
  meetingLabel: string;
  groups: { plan: ItinerarySuccess; arrivalTime: string; waitMinutes: number; finishLabel: string }[];
};
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const id = (value: unknown): value is string => typeof value === "string" && /^[A-Za-z0-9:_-]{1,200}$/.test(value);
const time = (value: unknown): value is string => typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
export const groupHour = (value: string) => Number(value.slice(0, 2)) + Number(value.slice(3)) / 60;
const date = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const epoch = Date.parse(`${value}T12:00:00Z`);
  return Number.isFinite(epoch) && new Date(epoch).toISOString().slice(0, 10) === value;
};

/** Shared arrangements are data only: no names, live coordinates or executable links. */
export function parseGroupJourney(raw: unknown): GroupJourneyArrangement | null {
  if (!record(raw) || raw.version !== 1 || typeof raw.slug !== "string" || !/^[a-z0-9-]{1,100}$/.test(raw.slug) || !date(raw.planDate) || !record(raw.preferences) || (raw.preferences.sport !== "ski" && raw.preferences.sport !== "mtb") || !record(raw.meeting) || !Array.isArray(raw.groups) || raw.groups.length !== 2) return null;
  const meeting = raw.meeting;
  if (!id(meeting.nodeId) || !time(meeting.time) || !Number.isInteger(meeting.pauseMinutes) || Number(meeting.pauseMinutes) < 0 || Number(meeting.pauseMinutes) > 60) return null;
  const groups: GroupJourneyMember[] = [];
  for (const group of raw.groups) {
    if (!record(group) || typeof group.ability !== "string" || !["beginner", "intermediate", "advanced", "expert"].includes(group.ability) || !id(group.startPoint) || !id(group.finishPoint) || !time(group.startTime) || !time(group.endTime)) return null;
    if (groupHour(group.startTime) >= groupHour(meeting.time) || groupHour(meeting.time) + Number(meeting.pauseMinutes) / 60 > groupHour(group.endTime)) return null;
    groups.push({ ability: group.ability as GroupJourneyMember["ability"], startPoint: group.startPoint, startTime: group.startTime, finishPoint: group.finishPoint, endTime: group.endTime });
  }
  const sport = raw.preferences.sport === "mtb" ? "mtb" : "ski";
  const initial = coercePreferences(raw.preferences, sport);
  const preferences = coercePreferences(raw.preferences, sport, stylePatchPathsFor(initial.dayStyle));
  return { version: 1, slug: raw.slug, planDate: raw.planDate, preferences, meeting: { nodeId: meeting.nodeId, time: meeting.time, pauseMinutes: Number(meeting.pauseMinutes) }, groups: groups as GroupJourneyArrangement["groups"] };
}

export function validGroupJourneyContext(raw: unknown): raw is GroupJourneyContext {
  return record(raw) && (raw.subgroup === 0 || raw.subgroup === 1) && Number.isInteger(raw.meetingLegIndex) && Number(raw.meetingLegIndex) >= 0 && Number(raw.meetingLegIndex) <= 400 && parseGroupJourney(raw.arrangement) !== null;
}

/** A fragment stays out of page requests and needs no public database write. */
export function groupJourneyFragment(arrangement: GroupJourneyArrangement): string {
  const parsed = parseGroupJourney(arrangement);
  if (!parsed) throw new Error("Choose a valid meeting and finish for both groups.");
  const value = encodeURIComponent(JSON.stringify(parsed));
  if (value.length > 12_000) throw new Error("This arrangement is too large to share.");
  return `#group=${value}`;
}
export function readGroupJourneyFragment(hash: string, slug: string): GroupJourneyArrangement | null {
  if (!hash.startsWith("#group=") || hash.length > 12_007) return null;
  try {
    const parsed = parseGroupJourney(JSON.parse(decodeURIComponent(hash.slice(7))));
    return parsed?.slug === slug ? parsed : null;
  } catch { return null; }
}

export function groupJourneyStorageKey(slug: string, sport: string): string {
  return `peakwave:group-journey:v1:${slug}:${sport}`;
}
